import React, { useEffect, useMemo, useState } from 'react';
import DsButton from '../../common/DsButton';
import { listArtistEventsByDate } from '../../../api/taskEvents';
import {
  MAX_VISIBLE_EVENTS_PER_DAY,
  weekdayLabels,
  formatYMD,
  formatYearMonthJP,
  getCalendarEventChipClass,
  getCalendarGridRange,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addMonths,
  isSameMonth,
  isSameDay,
  normalizeDateKey,
  buildEventsByDate,
} from '../../../hooks/adminEvents/eventListCalendarUtils';
import '../../../css/common/Calendar.css';

// 週グリッド生成用のローカル日付加算ヘルパー。
function addDays(date, amount) {
  const result = new Date(date);
  result.setDate(result.getDate() + amount);
  return result;
}

// admin の getEventDisplayTitle と同じ規則でチップ表示名を組み立てる。
function getEventDisplayTitle(event) {
  const base = event?.name || event?.event_name || event?.title || 'イベント名未設定';
  const performance = event?.performance_type;
  return [base, performance].filter(Boolean).join(' ');
}

export default function ArtistEventListCalendarView({ renderEventRow }) {
  const [month, setMonth] = useState(() => new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [eventsByDate, setEventsByDate] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const today = useMemo(() => new Date(), []);

  useEffect(() => {
    let cancelled = false;
    const fetchMonth = async () => {
      try {
        setLoading(true);
        setError(null);
        const { startDate, endDate } = getCalendarGridRange(month);
        const events = await listArtistEventsByDate({ startDate, endDate });
        if (cancelled) return;
        setEventsByDate(buildEventsByDate(events));
      } catch (err) {
        if (cancelled) return;
        console.error(err);
        setError('イベント情報の読み込みに失敗しました。');
        setEventsByDate({});
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchMonth();
    return () => {
      cancelled = true;
    };
  }, [month]);

  const weeks = useMemo(() => {
    const gridStart = startOfWeek(startOfMonth(month));
    const gridEnd = endOfWeek(endOfMonth(month));
    const days = [];
    let day = gridStart;
    while (day <= gridEnd) {
      days.push(day);
      day = addDays(day, 1);
    }
    const rows = [];
    for (let i = 0; i < days.length; i += 7) {
      rows.push(days.slice(i, i + 7));
    }
    return rows;
  }, [month]);

  const handlePrevMonth = () => {
    setMonth((prev) => addMonths(prev, -1));
    setSelectedDate(null);
  };

  const handleNextMonth = () => {
    setMonth((prev) => addMonths(prev, 1));
    setSelectedDate(null);
  };

  const selectedEvents = selectedDate
    ? eventsByDate[normalizeDateKey(selectedDate)] || []
    : [];

  return (
    <div className={`calendar-card calendar-card--list-page${loading ? ' calendar-card--loading' : ''}`}>
      <div className="calendar-card__header calendar-card__header--compact">
        <div className="calendar-month-nav calendar-month-nav--center">
          <DsButton
            type="button"
            variant="secondary"
            size="sm"
            icon
            className="calendar-nav-action"
            onClick={handlePrevMonth}
            aria-label="前の月へ"
          >
            ◀
          </DsButton>
          <div className="calendar-month-label">{formatYearMonthJP(month)}</div>
          <DsButton
            type="button"
            variant="secondary"
            size="sm"
            icon
            className="calendar-nav-action"
            onClick={handleNextMonth}
            aria-label="次の月へ"
          >
            ▶
          </DsButton>
        </div>
      </div>

      {loading && (
        <div className="calendar-progress" aria-hidden="true">
          <span />
        </div>
      )}
      {error && (
        <div className="calendar-events-panel__message calendar-events-panel__message--error calendar-inline-error">
          {error}
        </div>
      )}

      <div className="calendar-shell">
        <div className="calendar-weekdays">
          {weekdayLabels.map((label, index) => {
            const classes = ['calendar-weekday'];
            if (index === 0) classes.push('calendar-weekday--sun');
            if (index === 6) classes.push('calendar-weekday--sat');
            return (
              <div key={label} className={classes.join(' ')}>
                {label}
              </div>
            );
          })}
        </div>

        <div className="calendar-grid">
          {weeks.map((week, weekIndex) => (
            <div key={weekIndex} className="calendar-row">
              {week.map((day) => {
                const dateKey = normalizeDateKey(day);
                const inMonth = isSameMonth(day, month);
                const dayEvents = inMonth && dateKey ? eventsByDate[dateKey] || [] : [];
                const dayNumber = String(day.getDate());
                const weekday = day.getDay();

                const cellClasses = ['calendar-cell'];
                if (!inMonth) cellClasses.push('calendar-cell--other-month');
                if (weekday === 0) cellClasses.push('calendar-cell--sun');
                if (weekday === 6) cellClasses.push('calendar-cell--sat');
                if (selectedDate && isSameDay(day, selectedDate)) {
                  cellClasses.push('calendar-cell--selected');
                }
                if (inMonth && isSameDay(day, today)) {
                  cellClasses.push('calendar-cell--today');
                }

                const visibleEvents = dayEvents.slice(0, MAX_VISIBLE_EVENTS_PER_DAY);
                const remainingCount = dayEvents.length - visibleEvents.length;

                return (
                  <button
                    key={dateKey || day.toISOString()}
                    type="button"
                    className={cellClasses.join(' ')}
                    onClick={() => setSelectedDate(day)}
                  >
                    <div className="calendar-cell__date">{dayNumber}</div>
                    <div className="calendar-cell__events">
                      {visibleEvents.map((ev, index) => {
                        const fullLabel = getEventDisplayTitle(ev);
                        const chipClass = getCalendarEventChipClass(ev);
                        const chipKey = `${ev.event_uuid || 'ev'}-${fullLabel}-${index}`;
                        return (
                          <div key={chipKey} className={chipClass} data-full-label={fullLabel}>
                            <span className="event-chip__label">{fullLabel}</span>
                          </div>
                        );
                      })}
                      {remainingCount > 0 && (
                        <div className="event-chip event-chip--more">
                          <span className="event-chip__label">
                            +{remainingCount}件のイベント
                          </span>
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      <div className={`calendar-events-panel${!selectedDate ? ' calendar-events-panel--empty' : ''}`}>
        {!selectedDate && (
          <div className="calendar-events-panel__empty" role="status">
            <div className="calendar-events-panel__empty-title">日付を選択</div>
            <div className="calendar-events-panel__empty-description">
              選択した日の予定をここに表示します
            </div>
          </div>
        )}
        {selectedDate && (
          <>
            <div className="calendar-events-panel__title">
              {formatYMD(selectedDate)} のイベント
            </div>
            {selectedEvents.length === 0 ? (
              <div className="calendar-events-panel__message">
                この日に登録されているイベントはありません。
              </div>
            ) : (
              <div className="event-list-rows">
                {selectedEvents.map((ev, index) => renderEventRow(ev, index))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
